import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import {
  ConnectionsService,
  BookingRequestRead,
  BookingRequestReadStatusEnum,
  PaginatedBookingRequestReadList,
  BookingRequestsService,
  PaginatedConnectedFacilityOptimizedList,
} from '../../../../../js/api';
import type { ConnectedFacility } from '../../../../../js/api';
import { ProfileProgress } from '../../../../../js/app/components/ProfileProgress';
import { useCurrentUser } from '../../../../../js/app/hooks/auth';
import { useProvider } from '../../../../../js/app/hooks/providers';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { useUserProgress } from '../../../../../js/app/hooks/user-progress';
import { ProviderSchedule } from '../../../../../js/pages/ProviderDashboard/ProviderSchedule';
import { StatusCodes } from '../../../constants/workflows';
import { PROVIDER_CONNECTIONS_LIST_QUERY_KEY } from '../Connections/queryKeys';
import { AllSetStatus } from './AllSetStatus';
import { AssignmentsPendingProviderResponse } from './AssignmentsPendingProviderResponse';
import { AvailabilityUrlCopy } from './AvailabilityUrlCopy';
import { ConnectionsPendingProviderResponse } from './ConnectionsPendingProviderResponse';
import ConnectionsProviderActionRequested from './ConnectionsProviderActionRequested';
import { HomeSection } from './HomeSection';
import { ProfileCompletionTasks } from './ProfileCompletionTasks';

export function ProviderHomeWrapper() {
  const { user } = useCurrentUser();
  const { provider } = useProvider();
  const providerId = user?.provider_id;
  const { progress, isProfileComplete } = useUserProgress(
    user?.provider_completed_registration_steps
  );

  useDocumentTitle('Home');

  const connectionsQueryParams: Parameters<
    typeof ConnectionsService.connectionsConnectedFacilityList
  >[0] = {
    status: [StatusCodes.REQUESTED, StatusCodes.PROV_ACTION_REQ],
  };

  const assignmentsQueryParams: Parameters<typeof BookingRequestsService.bookingRequestsList>[0] = {
    // TODO: is there a managed enum for this somewhere?
    statusIn: ['PENDING'],
    date: 'UPCOMING',
  };

  const {
    data: connectionRequests = [],
    isLoading: isConnectionRequestsLoading,
    error: connectionRequestsError,
  } = useQuery({
    queryKey: [PROVIDER_CONNECTIONS_LIST_QUERY_KEY, connectionsQueryParams],
    queryFn: () => ConnectionsService.connectionsConnectedFacilityList(connectionsQueryParams),
    select: (data: PaginatedConnectedFacilityOptimizedList) => data.results as ConnectedFacility[],
    staleTime: 30_000,
  });

  const {
    data: assignments = [],
    isLoading: isAssignmentsLoading,
    error: assignmentsError,
  } = useQuery({
    queryKey: ['provider-booking-requests-initial', assignmentsQueryParams],
    queryFn: () => BookingRequestsService.bookingRequestsList(assignmentsQueryParams),
    select: (data: PaginatedBookingRequestReadList) => data.results as BookingRequestRead[],
    staleTime: 30_000,
  });

  const pendingAssignmentRequests = useMemo(
    () => assignments.filter((a) => a.status === BookingRequestReadStatusEnum.PENDING),
    [assignments]
  );

  const hasPendingProfileTasks = useMemo(() => {
    if (!provider) return false;

    const hasCv = Boolean(provider.cv);
    const references = Array.isArray(provider.references) ? provider.references : [];
    const professionalTraitsComplete = hasCv && references.length > 0;

    const licenses = Array.isArray(provider.licenses) ? provider.licenses : [];

    const isMedicalLicenseWithFile = (obj: unknown): boolean => {
      if (typeof obj !== 'object' || obj === null) return false;
      const o = obj as { [key: string]: unknown };
      const isMedical = typeof o['type'] === 'string' && o['type'] === 'MEDICAL';
      const hasFile = 'file' in o && Boolean(o['file']);
      return isMedical && hasFile;
    };

    const licensesComplete = licenses.some(isMedicalLicenseWithFile);
    const questionnaireComplete = Boolean(provider.completed_questionnaire);
    const serviceAgreementComplete = provider.needs_contract_signed === false;

    return (
      !professionalTraitsComplete ||
      !licensesComplete ||
      !questionnaireComplete ||
      !serviceAgreementComplete
    );
  }, [provider]);

  const connectionsPendingProviderResponse = useMemo(
    () => connectionRequests.filter((c) => c.current_status.code === StatusCodes.REQUESTED),
    [connectionRequests]
  );

  const connectionsProvActionReq = useMemo(
    () => connectionRequests.filter((c) => c.current_status.code === StatusCodes.PROV_ACTION_REQ),
    [connectionRequests]
  );

  // Combined loading state - show loading spinner until all API requests complete
  const isLoading = isConnectionRequestsLoading || isAssignmentsLoading;

  // Show the loading spinner while any API request is loading
  if (isLoading) {
    return (
      <Container
        className="content-wrapper justify-content-center align-items-center text-center"
        fluid
      >
        <Spinner
          animation="grow"
          aria-label="Loading"
          className="loading-spinner"
          variant="primary"
        />
      </Container>
    );
  }

  return (
    <div className="tw:p-4 tw:xl:px-0 tw:max-w-screen-lg tw:mx-auto tw:w-full">
      {/* Requires Attention Section */}
      {!isConnectionRequestsLoading &&
        !connectionRequestsError &&
        !assignmentsError &&
        (connectionsPendingProviderResponse.length > 0 ||
          pendingAssignmentRequests.length > 0 ||
          hasPendingProfileTasks ||
          !isProfileComplete) && (
          <HomeSection
            headingText=""
            sectionClassName="tw:mt-0"
            headingClassName="tw:text-warning-section-text"
            components={[
              // Show ProfileProgress if onboarding is not complete
              !isProfileComplete && (
                <div
                  key="profileProgress"
                  className="tw:bg-white tw:rounded-xl tw:p-4 tw:border tw:border-gray-200"
                >
                  <ProfileProgress progress={progress} />
                </div>
              ),
              // Show other components only if onboarding is complete
              isProfileComplete && (
                <ConnectionsProviderActionRequested
                  key="connectionsProvActionReq"
                  connectionsProvActionReq={connectionsProvActionReq}
                />
              ),
              isProfileComplete && (
                <ConnectionsPendingProviderResponse
                  key="connectionsPendingProviderResponse"
                  connectionsPendingProviderResponse={connectionsPendingProviderResponse}
                />
              ),
              isProfileComplete && (
                <AssignmentsPendingProviderResponse
                  key="assignmentsPendingProviderResponse"
                  assignmentsPendingProviderResponse={pendingAssignmentRequests}
                />
              ),
              isProfileComplete && <ProfileCompletionTasks key="profileCompletionTasks" />,
            ].filter(Boolean)}
            actionText=""
            actionFunction={undefined}
          />
        )}

      {/* All Set Status - shown when no actions require attention */}
      {!isConnectionRequestsLoading &&
        !connectionRequestsError &&
        !assignmentsError &&
        connectionsPendingProviderResponse.length === 0 &&
        pendingAssignmentRequests.length === 0 &&
        !hasPendingProfileTasks && (
          <div className="tw:my-6">
            <AllSetStatus
              providerDisplayName={`${provider?.prefix_name} ${provider?.preferred_name} ${provider?.last_name}`}
            />
          </div>
        )}

      {/* Availability Section */}
      {providerId && (
        <HomeSection
          headingText="Availability"
          components={[
            provider?.availability_url && (
              <AvailabilityUrlCopy
                key="availabilityUrlCopy"
                availabilityUrl={provider.availability_url}
              />
            ),
            <ProviderSchedule
              key="providerSchedule"
              readOnly={false}
              isBooking={false}
              onHomePage={true}
            />,
          ].filter(Boolean)}
          actionText=""
          actionFunction={undefined}
        />
      )}

      {/* TODO: decide what to do about pending connections, if anything */}
      {/* Pending Connections Section
      {!isConnectionRequestsLoading && !connectionRequestsError && (
        <HomeSection
          headingText="Pending Connections"
          components={[
            <ConnectionsPendingFacilityResponse
              key="connectionsPendingFacilityResponse"
              connectionsPendingFacilityResponse={connectionsPendingFacilityResponse}
              // connectionsPendingFacilityResponse={[]}
            />,
          ]}
          actionText="View All"
          actionFunction={() =>
            navigate('/app/provider/connection-requests?statusCategory=in_process')
          }
        />
      )}

      // TODO: should we add upcoming shifts?
      {/* Assignments Section */}
      {/* {!isAssignmentsLoading && !assignmentsError && (
        <HomeSection
          headingText="Assignments"
          components={[
            <AssignmentsPendingFacilityResponse
              key="assignmentsPendingFacilityResponse"
              assignments={pendingAssignmentRequests}
              // assignments={[]}
            />,
          ]}
          actionText="View All"
          actionFunction={() => navigate('/app/provider/booking-requests')}
        />
      )} */}
    </div>
  );
}

export default ProviderHomeWrapper;
